import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'irembogov-currency-format-input',
  templateUrl: './irembogov-currency-format-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IrembogovCurrencyFormatInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IrembogovCurrencyFormatInputComponent),
      multi: true,
    },
  ],
})
export class IrembogovCurrencyFormatInputComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy, Validator
{
  @Input() placeholder? = '';
  @Input() customformControl = new FormControl();
  @Input() requiredField = false;
  @Output() valueChange = new EventEmitter<number | null>();

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange = (value: unknown) => {};
  public _onTouch = (value: unknown) => {};
  private _onValidationChange = () => {};
  /* eslint-enable */

  private subscriptions = new Subscription();

  constructor(private readonly cdRef: ChangeDetectorRef) {}

  writeValue(value: string | number): void {
    if (value !== null && value !== undefined && value !== '') {
      const formatted = this.formatValue(value);
      this.customformControl.setValue(formatted, { emitEvent: false });
    } else {
      this.customformControl.setValue(value, { emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    if (this.customformControl.value) {
      this.customformControl.updateValueAndValidity({ emitEvent: true });
      this._onValidationChange();
    }
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidationChange = fn;
  }

  registerOnTouched(fn: (_: unknown) => void): void {
    this._onTouch = fn;
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this._onChange = fn;
  }

  validate(formControl: FormControl): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};
    this.cdRef.detectChanges();

    if (this.requiredField && !formControl.value) {
      validationErrors['required'] = true;
    }

    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value.replace(/[^0-9]/g, '');
    const formattedValue = this.format(rawValue);

    input.value = formattedValue;
    this.customformControl.setValue(formattedValue, { emitEvent: false });

    const numericValue = rawValue ? parseInt(rawValue, 10) : null;
    this._onChange(numericValue);
    this.valueChange.emit(numericValue);
  }

  formatValue(value: string | number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const strValue = String(value);
    const rawValue = strValue.replace(/[^0-9]/g, '');
    return this.format(rawValue);
  }

  format(value: string): string {
    if (!value) return '';
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
